import {
  Button as ButtonChakraUI,
  type ButtonProps as ChakraButtonProps,
  type ResponsiveValue,
} from '@chakra-ui/react'
import { forwardRef, type LegacyRef } from 'react'

import { get } from 'lodash'

interface HoverColorsButtonProps {
  bg?: ResponsiveValue<string>
  color?: ResponsiveValue<string>
  borderColor?: ResponsiveValue<string>
}
interface ButtonProps extends ChakraButtonProps {
  hoverColors?: HoverColorsButtonProps
  disabledHoverColors?: HoverColorsButtonProps
}

const Button = (props: ButtonProps, ref: LegacyRef<HTMLButtonElement>) => {
  const { bgColor, textColor, borderColor, hoverColors, disabledHoverColors, ...restProps } = props
  let colors = {
    bg: bgColor,
    text: textColor,
    border: borderColor,
  }
  let hover: HoverColorsButtonProps = {
    bg: colors.border,
    color: colors.bg,
    borderColor: colors.bg,
  }
  if (hoverColors) {
    hover = {
      ...hover,
      ...hoverColors,
    }
  }
  if (get(restProps, 'isDisabled')) {
    colors = {
      bg: 'gray.300',
      text: 'white',
      border: 'white',
    }
    hover = {
      bg: colors.border,
      color: colors.bg,
      borderColor: colors.bg,
    }
    if (disabledHoverColors) {
      hover = {
        ...hover,
        ...disabledHoverColors,
      }
    }
  }
  return (
    <ButtonChakraUI
      ref={ref}
      color={colors.text}
      bg={colors.bg}
      border="1px"
      borderColor={colors.border}
      _hover={hover}
      fontSize={12}
      {...restProps}>
      {restProps.children}
    </ButtonChakraUI>
  )
}

const ButtonPrimary = forwardRef(
  (props: ButtonProps & { reverse?: boolean }, ref: LegacyRef<HTMLButtonElement>) => {
    const { reverse = false, ...rest } = props
    return reverse
      ? Button(
          {
            ...rest,
            bgColor: 'white',
            textColor: 'michelinPrimary',
            borderColor: 'michelinPrimary',
          },
          ref
        )
      : Button(
          { ...rest, bgColor: 'michelinPrimary', textColor: 'white', borderColor: 'white' },
          ref
        )
  }
)
ButtonPrimary.displayName = 'ButtonPrimary'

const ButtonSecondary = forwardRef((props: ButtonProps, ref: LegacyRef<HTMLButtonElement>) => {
  return Button(
    {
      ...props,
      bgColor: 'white',
      textColor: '#4D4D4D',
      borderColor: '#AF8D5A',
      hoverColors: { bg: '#F5F3F1', color: '#4D4D4D', borderColor: '#AF8D5A' },
    },
    ref
  )
})
ButtonSecondary.displayName = 'ButtonSecondary'

const buttonTabCommonStyle = {
  borderBottomLeftRadius: '0px',
  borderBottomRightRadius: '0px',
  fontSize: '12px !important',
  textTransform: 'capitalize',
}

const ButtonTab = forwardRef((props: ButtonProps, ref: LegacyRef<HTMLButtonElement>) => {
  const { isActive = false, ...rest } = props
  return isActive
    ? Button(
        {
          ...rest,
          ...buttonTabCommonStyle,
          bottom: '-1px',
          borderBottomColor: 'white',
          _hover: { color: 'michelinPrimary' },
          bgColor: 'white',
          textColor: 'michelinPrimary',
          borderColor: 'gray.200',
        },
        ref
      )
    : Button(
        {
          ...rest,
          ...buttonTabCommonStyle,
          _hover: { color: 'gray.900' },
          bgColor: 'gray.50',
          textColor: 'gray.600',
          borderColor: 'transparent',
        },
        ref
      )
})
ButtonTab.displayName = 'ButtonTab'

export { ButtonPrimary, ButtonSecondary, ButtonTab, type ButtonProps }
